/**
 * Destination autocomplete and geo API client.
 *
 * Autocomplete: GET /api/v2/destinations/autocomplete — returns slim suggestions, no geoposition.
 * Geo lookup:   GET /api/v2/destinations/geo?google_place_id=… — called on selection to get
 *               geoposition so the map can jump to the chosen destination.
 */

import axios from 'axios';
import type { DestinationAutocompleteResult, GeoJSONPoint, PrimaryDestinationType } from '@/types/city';
import { DESTINATION_MIN_QUERY_LENGTH } from '@/constants/destinationSearch';
import { getFirstPhotoUrl } from '@/utils/photoUtils';
import { debug, error } from '@/utils/logger';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;
const DESTINATION_AUTOCOMPLETE_URL = `${API_URL}/api/v2/destinations/autocomplete`;
const DESTINATION_GEO_URL = `${API_URL}/api/v2/destinations/geo`;

export interface DestinationAutocompleteParams {
  type: PrimaryDestinationType;
  q: string;
  lat?: number;
  long?: number;
}

export interface DestinationGeoResult {
  google_place_id: string;
  display_name?: string;
  geoposition?: GeoJSONPoint;
  photo?: {
    name?: string;
    photo_reference?: string;
    author?: { displayName?: string | null; uri?: string | null } | null;
  };
}

export function getPhotoUrlFromGeo(
  geo: DestinationGeoResult | null | undefined,
  size = 720
): string | undefined {
  if (!geo?.photo) return undefined;
  return getFirstPhotoUrl({ photos: [geo.photo] }, size) || undefined;
}

/**
 * Searches for destination suggestions (city, locality, or country).
 *
 * Requires q to be at least DESTINATION_MIN_QUERY_LENGTH characters. Returns an empty array on error
 * or when q is too short. Results do not include geoposition; call
 * getDestinationGeo when the user selects a suggestion to get geoposition
 * for map centering.
 */
export async function searchDestination(
  params: DestinationAutocompleteParams
): Promise<DestinationAutocompleteResult[]> {
  const { type, q, lat, long } = params;

  if (!q || q.trim().length < DESTINATION_MIN_QUERY_LENGTH) {
    return [];
  }

  try {
    debug('[searchDestination] Requesting destination autocomplete', { type, q, lat, long });

    const response = await axios.get<DestinationAutocompleteResult[]>(
      DESTINATION_AUTOCOMPLETE_URL,
      {
        params: {
          type,
          q: q.trim(),
          ...(lat !== undefined && { lat }),
          ...(long !== undefined && { long }),
        },
      }
    );

    const results = Array.isArray(response.data) ? response.data : [];

    debug('[searchDestination] Results received', { type, count: results.length });

    return results;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const status = typeof (err as { response?: { status?: number } })?.response?.status === 'number'
      ? (err as { response: { status: number } }).response.status
      : undefined;
    const responseData = (err as { response?: { data?: unknown } })?.response?.data;
    error('[searchDestination] Error fetching destination autocomplete', {
      type,
      q,
      message,
      status,
      responseData,
    });
    return [];
  }
}

/**
 * Fetches geoposition for a selected destination suggestion.
 *
 * Called once when the user picks a suggestion from the autocomplete dropdown.
 * Returns geoposition as a GeoJSON Point ({ type: 'Point', coordinates: [lng, lat] })
 * for use as the map center. Returns null if the place is not found or has no location.
 */
export async function getDestinationGeo(
  googlePlaceId: string
): Promise<DestinationGeoResult | null> {
  if (!googlePlaceId) return null;

  try {
    debug('[getDestinationGeo] Fetching geoposition', { googlePlaceId });

    const response = await axios.get<DestinationGeoResult>(DESTINATION_GEO_URL, {
      params: { google_place_id: googlePlaceId },
    });

    debug('[getDestinationGeo] Geoposition received', {
      googlePlaceId,
      coordinates: response.data?.geoposition?.coordinates,
    });

    return response.data ?? null;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const status = typeof (err as { response?: { status?: number } })?.response?.status === 'number'
      ? (err as { response: { status: number } }).response.status
      : undefined;
    error('[getDestinationGeo] Error fetching destination geoposition', {
      googlePlaceId,
      message,
      status,
    });
    return null;
  }
}
